// @ts-nocheck
import Icon from 'atoms/icons/icon';
import jwt_decode from 'jwt-decode';
import _ from 'lodash';
import { models } from 'powerbi-client';
import { PowerBIEmbed } from 'powerbi-client-react';
import React from 'react';
import { isMobile } from 'react-device-detect';
import './intelligence.scss';
import * as services from './service';
import Stack from 'molecules/stack/stack';
import StackItem from 'molecules/stackItem/stackItem';
import Divider from 'atoms/divider/divider';
import DropDown from 'atoms/dropdown/dropdown';
import Label from 'atoms/label/label';
import PageLayout from 'templates/pageLayout';

const defaultReportSettings = {
	filterPaneEnabled: true,
	personalBookmarksEnabled: true,
	persistentFiltersEnabled: true,
	panes: {
		bookmarks: { visible: false },
		fields: { expanded: false },
		filters: { expanded: false, visible: true },
		pageNavigation: {
			visible: true,
			position: models.PageNavigationPosition.Left,
		},
		selection: { visible: false },
		syncSlicers: { visible: false },
		visualizations: { expanded: false },
	},
};

const defaultReportConfig = {
	type: 'report',
	embedUrl: undefined,
	tokenType: models.TokenType.Aad,
	accessToken: undefined,
	id: undefined,
};

const eventHandlers = new Map([
	['loaded', function () {}],
	['rendered', function () {}],
	['error', function (event: any) {}],
]);

const REPORT_DROPDOWN_ITEMS = [
	{ displayName: 'Bookmarks', value: 'bookmarks_toggle' },
	{ displayName: 'Full Screen', value: 'full_screen_report' },
	{ displayName: 'Print', value: 'print_report' },
	{ displayName: 'Refresh', value: 'refresh' },
];

const Intelligence = () => {
	const [reportConfig, setReportConfig] = React.useState(defaultReportConfig);
	const [accessToken, setAccessToken] = React.useState('');
	const [workspaces, updateWorkspaces] = React.useState<any[]>([]);
	const [tokenExpiryListener, updateTokenExpiryListener] = React.useState(null);
	const [currentReport, setCurrentReport] = React.useState(null);
	const [isFetchingToken, setIsFetchingToken] = React.useState(false);

	const getReportData = React.useCallback(async () => {
		if (isFetchingToken) return; // Prevent multiple calls
		setIsFetchingToken(true);

		try {
			const data = await services.getReportData();
			if (data.embedToken && data.embedToken !== accessToken) {
				setAccessToken(data.embedToken);
			}

			if (data.workspaces) {
				let workspaces = _.sortBy(data.workspaces, ['name']).map((workspace) => ({
					...workspace,
					reports: _.sortBy(workspace.reports, ['name']),
				}));
				workspaces = _.reject(workspaces, (w) => !w.reports.length);

				if (!currentReport?.config?.id) {
					const initialReportData = workspaces[0]?.reports?.[workspaces[0]?.reports?.length - 1];
					if (initialReportData) {
						setReportConfig((prevConfig) => ({
							...prevConfig,
							embedUrl: initialReportData.embedUrl,
							id: initialReportData.id,
						}));
					}
				}

				updateWorkspaces(workspaces);
			}
		} catch (error) {
			console.error('Error fetching report data:', error);
		} finally {
			setIsFetchingToken(false);
		}
	}, [accessToken, currentReport?.config?.id, isFetchingToken]);

	React.useEffect(() => {
		const handleVisibilityChange = () => {
			if (!document.hidden) {
				try {
					const expiration = accessToken ? jwt_decode<any>(accessToken).exp * 1000 : 0;
					if (Date.now() >= expiration) {
						getReportData();
					}
				} catch (error) {
					console.error('Error decoding token:', error);
				}
			}
		};
		handleVisibilityChange();

		// document.addEventListener("visibilitychange", handleVisibilityChange);
		// return () =>
		//   document.removeEventListener("visibilitychange", handleVisibilityChange);
	}, [accessToken, getReportData]);

	React.useEffect(() => {
		if (!accessToken) return;

		let tokenExpiryTime;
		try {
			const expiration = jwt_decode<any>(accessToken).exp * 1000;
			tokenExpiryTime = expiration - Date.now();
		} catch (error) {
			console.error('Error decoding token:', error);
			return;
		}

		if (tokenExpiryListener) clearTimeout(tokenExpiryListener);

		const timeout = setTimeout(
			() => {
				getReportData();
			},
			Math.max(tokenExpiryTime, 1000)
		); // Ensure a minimum delay

		updateTokenExpiryListener(timeout);

		return () => clearTimeout(timeout);
	}, [accessToken, getReportData]);

	const handleReportChange = (item) => {
		const selectedReport = workspaces.flatMap((w) => w.reports).find((r) => r.id === item.value);
		if (selectedReport) {
			setReportConfig((prevConfig) => ({
				...prevConfig,
				embedUrl: selectedReport.embedUrl,
				id: selectedReport.id,
			}));
		}
	};

	const menuItemClickHandler = (info: any) => {
		switch (info.key) {
			case 'print_report':
				if (currentReport) {
					currentReport.print();
				}
				break;
			case 'full_screen_report':
				if (currentReport) {
					currentReport.fullscreen();
				}
				break;
			case 'bookmarks_toggle':
				if (currentReport) {
					currentReport.updateSettings({ bookmarksPaneEnabled: true });
				}
				break;
			case 'refresh':
				if (currentReport) {
					currentReport.refresh();
				}
				break;
			default:
				break;
			//   default:
			//     let report;
			//     for (const workspace of workspaces) {
			//       report = _.find(workspace.reports, { id: info.key });
			//       if (report) {
			//         break;
			//       }
			//     }

			//     if (report) {
			//       setReportConfig({
			//         ...reportConfig,
			//         embedUrl: report.embedUrl,
			//         id: report.id,
			//       });
			//     }
		}
	};

	return (
		<div
			id="intelligence"
			className={workspaces.length && reportConfig?.id && reportConfig?.embedUrl ? 'report-loaded' : ''}
		>
			<PageLayout className="p-3">
				<Stack isVertical gutter="small">
					<StackItem>
						<Stack>
							<StackItem isGrow>
								<Stack gutter="medium">
									{workspaces.map((workspace) => {
										const selectedOption = workspace.reports.find((r) => r.id === reportConfig.id);
										return (
											<StackItem key={workspace.name}>
												<Stack isVertical>
													<StackItem>
														<Label> {workspace.name} </Label>
													</StackItem>
													<StackItem>
														<DropDown
															placeholder="Select"
															options={workspace.reports.map((report) => ({
																displayName: report.name,
																value: report.id,
															}))}
															updateHandler={handleReportChange}
															selectedOption={
																selectedOption
																	? {
																			displayName: selectedOption.name,
																			value: selectedOption.id,
																		}
																	: {}
															}
														/>
													</StackItem>
												</Stack>
											</StackItem>
										);
									})}
								</Stack>
							</StackItem>
							<StackItem position="center">
								<DropDown
									placeholder="Report Options"
									options={REPORT_DROPDOWN_ITEMS}
									updateHandler={(item: any) => menuItemClickHandler({ key: item.value })}
								/>
							</StackItem>
						</Stack>
					</StackItem>
					<StackItem isStretch>
						<Divider />
					</StackItem>
					<StackItem isGrow isStretch>
						<PowerBIEmbed
							embedConfig={{
								...reportConfig,
								accessToken,
								settings: defaultReportSettings,
							}}
							eventHandlers={
								new Map([
									['loaded', () => console.log('Report loaded')],
									['rendered', () => console.log('Report rendered')],
									['error', (event) => console.error('Report error:', event)],
								])
							}
							cssClassName={'report-style-class'}
							getEmbeddedComponent={setCurrentReport}
						/>
					</StackItem>
				</Stack>
			</PageLayout>
		</div>
	);
};

export default Intelligence;
